import { useEffect, useId, useState } from 'react';
import { IContentItem, IContentItemElements } from '@kontent-ai/delivery-sdk';
import { config as configs } from '../../src/index';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';
import { hasValue } from '@r-and-a-shared-ui/utils';

type AsyncComponentProps = {
  kontentItem: IContentItem<any>;
  contentType?: keyof typeof configs;
  locale?: string;
  codename?: string;
  children?: React.ReactNode;
  depthParameter?: number;
  shouldFetchData?: boolean;
};

export const AsyncComponent = ({
  kontentItem,
  contentType,
  locale,
  children,
  depthParameter = 2,
  shouldFetchData = true,
}: AsyncComponentProps) => {
  const uniqKey = useId();
  const [item, setItem] = useState<
    IContentItem<IContentItemElements> | null | undefined
  >(kontentItem);
  const [isDataFetched, setIsDataFetched] = useState(true);

  const config: any = contentType ? configs?.[contentType] : undefined;

  const { data } = api.kontent.itemWithLinkedItems.useQuery(
    {
      codename: isDataFetched ? '' : kontentItem?.system?.codename,
      depthParameter: config?.depthParameter || depthParameter,
    },
    { enabled: shouldFetchData },
  );

  useEffect(() => {
    if (hasValue(data)) {
      setItem(data);
      setIsDataFetched(true);
    }
  }, [data]);

  useEffect(() => {
    const elements = kontentItem?.elements ?? {};
    const elementsKeys = Object.keys(elements);

    for (const key of elementsKeys) {
      const element = elements[key];
      if (!element?.linkedItems?.length && element?.value?.length > 0) {
        setIsDataFetched(false);
        return;
      }
    }
  }, [kontentItem]);

  if (!item || !config?.component) {
    return null;
  }

  const Component = config.component?.();
  if (!Component) {
    return null;
  }

  try {
    const mappedProps = config.mapEntryToProps(item as IContentItem<any>);
    const kontentItemId = item?.system?.id;
    const key = `${uniqKey}-${contentType}-${kontentItemId}`;

    return (
      <Component {...mappedProps} key={key} kontentItemId={kontentItemId}>
        {children}
      </Component>
    );
  } catch (error) {
    console.error('AsyncComponent error:', error);
    return null;
  }
};

export default AsyncComponent;
